import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  DialogActions,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { ThemeContext } from "../../Homepage";
import { useSelector } from "react-redux";
import CustomCancelButton from "../../CommonComponents/CustomCancelButton";
import InputField from "../../CommonComponents/InputField";
import PasswordField from "./PasswordField";
import PhoneNumberField from "./PhoneNumberField";
import SelectInputField from "../../CommonComponents/SelectInputField";
import { userAccessService } from "../../../Restclient/datacollection";
import Spinner from "../AdminDashboard/Spinner";

const passwordRules = [
  {
    label: "At least 7 characters",
    test: (pw) => pw.length >= 7,
  },
  {
    label: "At least 1 uppercase letter",
    test: (pw) => /[A-Z]/.test(pw),
  },
  {
    label: "At least 1 special character",
    test: (pw) => /[!@#$%^&*(),.?\":{}|<>]/.test(pw),
  },
];

const UserDetailsView = ({
  user,
  open,
  actionType,
  onClose,
  onSave,
  rolesLst,
  userRoleName,
  onDeleteConfirm,
  updateUserProjects,
  nodeUsed
}) => {
  const { theme } = useContext(ThemeContext);
  const [userData, setUserData] = useState({});
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmPassword,setConfirmPassword]=useState('')
  const [Projects, setProjects] = useState([]);
  const userdetails = useSelector((state) => state.logindata.userdetails)
   const {User_Role_ID,Organizationid,Projectlist} = useSelector((state) => state.logindata.userdetails);
   const [isLoading, setIsLoading] = useState(false);


  // Add this function
  const getPasswordValidation = (password) =>
    passwordRules.map((rule) => ({
      label: rule.label,
      valid: rule.test(password),
    }));
    
  const fetchAvailableProjects = async () => {
    try {
      setIsLoading(true);
      let projectID = ((userdetails?.User_Role_Details?.Name == "EA_ADMIN") || (userdetails?.User_Role_Details?.Name == "ORGANIZATION_ADMIN")) ? "All": userdetails?.Projectlist[0];
      let organizationID = (userdetails?.User_Role_Details?.Name == "EA_ADMIN")? "All":Organizationid;
      
      const projects = await userAccessService.getAvailableProjects(projectID,user?.organizationID,user?.userRoleID);
      setProjects(projects?.data.Projects);
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    if (user && actionType !== "add") {
      setUserData(user);
    } else {
      resetForm();
    }

    if(user && actionType === "assignProject") {
      fetchAvailableProjects();
    }
  }, [user, actionType]);

  const resetForm = () => {
    setUserData({
      userID: "",
      projectIDArray: [],
      organizationID: "",
      userName: "",
      userRoleID: "",
      email: "",
      mobile: "",
      countryCode: "",
      landline: "",
      addressLine1: "",
      addressLine2: "",
      addressLine3: "",
      description: "",
      state: "active",
      password: "",
      currentInvalidLoginAttemptCount: 0,
      lastInvalidLoginAttempt: "0",
      pastPasswords: [],
      forgotPasswordEnabled: false,
      userAccountDisabled: false,
      passwordExpired: false,
      passwordUpdatedAt: "0",
      passwordModificationAllowed: false,
      passwordExpirationEnabled: false,
    });
    setErrors({});
  };

  const handleChange = (field, value) => {
    if (field === "mobile") {
      setUserData((prev) => ({
        ...prev,
        countryCode: value.countryCode, 
        mobile: value.phoneNumber, 
      }));
    } else {
      setUserData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleClose = () => {
    if (actionType !== "view") {
      resetForm();
    }
    onClose();
  };

  const validateFields = () => {
    const newErrors = {};
    const emailRegex = /^(?=.{5,320}$)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const userNameRegex = /^(?! )[A-Za-z0-9 _-]{5,30}(?<! )$/;
    const mobileRegex = /^\d{10}$/;
    const countryCodeRegex = /^\d{1,4}$/;
    const landlineRegex = /^(\d{10,15})?$/;
    const addressRegex = /^[a-zA-Z0-9\s.,\-\/]{5,100}$/;
    const descriptionRegex = /^.{5,30}$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{10,128}$/;

    
     // Validate userName
  if (!userData.userName || !userNameRegex.test(userData.userName)) {
    newErrors.userName =
      "User Name must be 5-30 characters long, no leading/trailing spaces, and can include letters, numbers, hyphens, underscores, and spaces.";
  }

  // Validate email
  if (!userData.email || !emailRegex.test(userData.email)) {
    newErrors.email = "Invalid email address. Must be 5-320 characters long and match the email format.";
  }

  // Validate mobile
  if (!userData.mobile || !mobileRegex.test(userData.mobile)) { 
    newErrors.mobile = "Mobile number must be 10 digits long with no spaces or special characters.";
  }

  // Validate countryCode
  if (!userData.countryCode || !countryCodeRegex.test(userData.countryCode)) {
    newErrors.countryCode = "Country code must be 1-4 digits long and contain only numbers.";
  }

  // Validate landline
  if (userData.landline && !landlineRegex.test(userData.landline)) {
    newErrors.landline = "Landline must be 10-15 digits long and contain only numbers.";
  }

  // Validate addressLine1
  if (!userData.addressLine1 || !addressRegex.test(userData.addressLine1)) {
    newErrors.addressLine1 =
      "Address Line 1 must be 5-100 characters long and can include letters, numbers, spaces, and symbols like ., - /.";
  }

  // Validate addressLine2 (optional)
  if (userData.addressLine2 && !addressRegex.test(userData.addressLine2)) {
    newErrors.addressLine2 =
      "Address Line 2 must be 5-100 characters long and can include letters, numbers, spaces, and symbols like ., - /.";
  }

  // Validate addressLine3 (optional)
  if (userData.addressLine3 && !addressRegex.test(userData.addressLine3)) {
    newErrors.addressLine3 =
      "Address Line 3 must be 5-100 characters long and can include letters, numbers, spaces, and symbols like ., - /.";
  }

  // Validate description
  // if (!userData.description || !descriptionRegex.test(userData.description)) {
  //   newErrors.description = "Description must be 5-30 characters long.";
  // }

  // Validate userRoleID
  if (!userData.userRoleID || userData.userRoleID.length !== 36) {
    newErrors.userRoleID = "Please Assign a role.";
  }

  // Validate password (only in add mode)
  if (actionType === "add" && (!userData.password || !passwordRegex.test(userData.password))) {
    newErrors.password =
      "Password must be 10–128 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.";
  }

  // Validate confirmPassword (only in add mode)
  if (actionType === "add" && userData.password !== confirmPassword) {
    newErrors.confirmPassword = "Passwords do not match.";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};
 

  const handleSave = () => {
    if (validateFields()) {
      onSave(userData);
      resetForm();
      onClose();
    }
  };

  const handleAssignProjects = async () => {
    await updateUserProjects(userData,userData?.projectIDArray);
    onClose();
  }

  const handleDelete = () => {
    onDeleteConfirm(user?.userID);
    onClose();
  };

  const getNameById = (id, list) => {
    const item = list.find((role) => role.UserRoleID === id);
    return item ? `${item.RoleLevel} (${item.Name})` : "N/A";
  };

  const userDetailsFields = [
    { label: "User Name", key: "userName" },
    { label: "Email", key: "email" },
    { label: "Mobile", key: "mobile" },
    { label: "Landline", key: "landline" },
    { label: "Address Line 1", key: "addressLine1" },
    { label: "Address Line 2", key: "addressLine2" },
    { label: "Address Line 3", key: "addressLine3" },
    { label: "Description", key: "description" },
    {
      label: "Role",
      key: "userRoleID",
      format: (value) => getNameById(value, rolesLst),
    },
    {
      label: "User Account Status",
      key: "userAccountDisabled",
      format: (value) => (value ? "Inactive" : "Active"),
    },
    {
      label: "Password Modification Allowed",
      key: "passwordModificationAllowed",
      format: (value) => (value ? "Yes" : "No"),
    },
    {
      label: "Password Expiration Enabled",
      key: "passwordExpirationEnabled",
      format: (value) => (value ? "Yes" : "No"),
    }
  ];

  const fields = [
    { label: "User Name", field: "userName", type: "text", required: true },
    { label: "Email", field: "email", type: "text", required: true },
    { label: "Landline", field: "landline", type: "text", required: true },
    { label: "Address Line 1", field: "addressLine1", type: "text", required: true },
    { label: "Address Line 2", field: "addressLine2", type: "text", required: false },
    { label: "Address Line 3", field: "addressLine3", type: "text", required: false },
    { label: "Description", field: "description", type: "text", required: false },
  ];

  const actionTypeLabels = {
    add: "Add User Details",
    edit: "Edit User Details",
    delete: "Delete User Details",
    view: "View User Details",
    assignProject: "Assign Projects",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          backgroundColor: theme === "dark" ? "#252525" : "#ffffff",
          boxShadow: "none",
          overflowY: "scroll",
          scrollbarWidth: "thin",
          minWidth: actionTypeLabels[actionType] === "Assign Projects" ? "800px" : "600px",
          maxWidth: "600px",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme === "dark" ? "#252525" : "#ffffff",
          color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
          margin: "8px 0px",
          fontWeight: "400",
          padding: "0px 16px",
          height: "28px",
          color: "#7E7E7E",
          fontWeight: "700",
          fontSize: "24px",
        }}
      >
        {actionTypeLabels[actionType]}
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: theme === "dark" ? "#252525" : "#ffffff",
          color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
          margin: "8px 0px",
          fontWeight: "400",
          overflow: "unset",
          padding: "24px",
          scrollbarWidth: "thin",
        }}
      >
        {(actionType === "add" || actionType === "edit") && (
          <Box>
            {fields.map(({ label, field, type, required }) => (
              <InputField
                key={field}
                label={label}
                required={required}
                value={userData[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                error={!!errors[field]}
                fullWidth
                type={type}
                helperText={errors[field]}
                style={{
                  color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                  width: "100%",
                }}
                disabled={actionType === "edit" && (field === "userName" || field === "email")}
                  autoComplete="off"
          
              />
            ))}
            {actionType === "add" && (
              <>
                <PasswordField
                  label="Password"
                  required
                  value={userData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password}
                  style={{
                    color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                    width: "100%",
                  }}
                  autoComplete="off"
                />
                <PasswordField
                  label="Confirm Password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  style={{
                    color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                    width: "100%",
                  }}
                  autoComplete="off"
                />
              </>
            )}
            <PhoneNumberField
              value={userData.mobile}
              onChange={(value) => handleChange("mobile", value)}
              error={!!errors.mobile}
              helperText={errors.mobile}
              style={{
                color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                width: "100%",
              }}
            />
            <SelectInputField
              label="Role"
              required
              value={userData.userRoleID || ""}
              onChange={(e) => handleChange("userRoleID", e.target.value)}
              options={rolesLst
                ?.filter((role) => {
                  const excludedLevelsForOrg = ["Level 1", "Level 4", "Level 5", "Level 6", "Level 7", "Level 8", "Level 9", "Level 10"];
                  const excludedLevelsForProject = ["Level 1", "Level 2", "Level 3"];
                  
                  if (nodeUsed === "organization" || nodeUsed === "sub-organization") {
                      return !excludedLevelsForOrg.includes(role.RoleLevel);
                  } else if (nodeUsed === "Project") {
                      return !excludedLevelsForProject.includes(role.RoleLevel);
                  }
                  return true;
                })
                .map((role) => ({
                  value: role.UserRoleID,
                  label: `${role.RoleLevel} (${role.Name})`,
                }))}
              style={{
                color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                width: "100%",
              }}
              error={!!errors.userRoleID}
              helperText={errors.userRoleID}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userData.userAccountDisabled}
                    onChange={(e) =>
                      handleChange("userAccountDisabled", e.target.checked)
                    }
                  />
                }
                label="User Account Disabled"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userData.passwordModificationAllowed}
                    onChange={(e) =>
                      handleChange("passwordModificationAllowed", e.target.checked)
                    }
                  />
                }
                label="Password Modification Allowed"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userData.passwordExpirationEnabled}
                    onChange={(e) =>
                      handleChange("passwordExpirationEnabled", e.target.checked)
                    }
                  />
                }
                label="Password Expiration Enabled"
              />
              {/* {
                actionType === "add" && (
                  <FormControlLabel
                  control={
                    <Checkbox
                      checked={userData.sendPasswordByEmail}
                      onChange={(e) =>
                        handleChange("sendPasswordByEmail", e.target.checked)
                      }
                    />
                  }
                  label="Share Password by Email"
                />
                )
              } */}
            </Box>
          </Box>
        )}
        {actionType === "view" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              padding: "16px",
              alignItems: "flex-start",
              backgroundColor: theme === "dark" ? "#2c2c2c" : "#f9f9f9",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                paddingBottom: "8px",
              }}
            >
              User Details
            </Typography>
            {userDetailsFields.map(({ label, key, format }) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderColor: theme === "dark" ? "#444" : "#ddd",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                  }}
                >
                  {label}:
                </Typography>
                <Typography
                  sx={{
                    color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                  }}
                >
                  {format ? format(userData[key]) : userData[key] || "N/A"}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {actionType === "delete" && (
          <Typography>
            Are you sure you want to delete the User{" "}
            <strong>{userData.userName}</strong>?
          </Typography>
        )}
        {actionType === "assignProject" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "24px",
                justifyContent: "space-between",
              }}
            >
              {/* Left Side: User Details */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: theme === "dark" ? "#2c2c2c" : "#f9f9f9",
                  padding: "16px",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                    paddingBottom: "8px",
                  }}
                >
                  User Details
                </Typography>
                <Typography>
                  <strong>Name:</strong> {user.userName}
                </Typography>
                <Typography>
                  <strong>Role:</strong> {userRoleName}
                </Typography>
                <Typography>
                  <strong>Email:</strong> {user.email}
                </Typography>
                <Typography>
                  <strong>Mobile:</strong> {user.mobile || "N/A"}
                </Typography>
              </Box>

              {/* Right Side: Project Selection */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: theme === "dark" ? "#2c2c2c" : "#f9f9f9",
                  padding: "16px",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                    paddingBottom: "8px",
                  }}
                >
                  Assign Projects 
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                { isLoading &&  <Spinner size="40px" color="blue" speed="0.8s"/>}
                  
                 {!isLoading && Array.isArray(Projects) && Projects.length > 0 && Projects.map((project) => (
                    <FormControlLabel
                      key={project.id}
                      control={
                        <Checkbox
                          checked={userData.projectIDArray.includes(project.ProjectId)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setUserData((prev) => ({
                              ...prev,
                              projectIDArray: isChecked
                                ? [...prev.projectIDArray, project.ProjectId]
                                : prev.projectIDArray.filter((id) => id !== project.ProjectId),
                            }));
                          }}
                        />
                      }
                      label={project.Projectname}
                      sx={{
                        color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
        )}
      </DialogContent>
      <DialogActions
        style={{
          backgroundColor: theme === "dark" ? "#252525" : "#ffffff",
          color: theme === "dark" ? "#FFFFFF" : "#3c3c3c",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {actionType === "delete" && (
          <Button
            onClick={handleDelete}
            sx={{
              width: "137px",
              borderRadius: "20px",
            }}
            variant="outlined"
            color="error"
          >
            Delete
          </Button>
        )}
         {(actionType === "add" || actionType === "edit" || actionType === "assignProject") && (
          <button
            onClick={actionType === "assignProject"? handleAssignProjects : handleSave}
            style={{
              width: "125px",
              height: "35px",
              fontSize: "18px",
              backgroundColor: "rgb(0, 92, 142)",
              color: "rgb(255, 255, 255)",
              cursor: "pointer",
              border: "none",
              borderRadius: "25px",
            }}
          >
            {actionType === "add" ? "Add User" : "Save"}
          </button>
        )}
        {/*<CustomCancelButton width={"125px"} onClick={handleClose}>
          {actionType === "delete" ? "Cancel" : "Close"}
        </CustomCancelButton>
        */}
        <CustomCancelButton buttonText={actionType === "delete" ? "Cancel" : "Close"} onClick={handleClose} width={"125px"}/>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsView;